import React, { useEffect, useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    useTheme,
} from '@mui/material'
import { useSharedStyles } from '@/pages/Settings/useSharedStyles'
import { UAParser } from 'ua-parser-js'
import packageJson from '../../../../package.json'
import { useTranslation } from 'react-i18next'

const AboutApplicationSection: React.FC = () => {
    const { t: translate } = useTranslation()
    const { appVersion, dependencies } = packageJson
    const { tableRowStyle } = useSharedStyles()
    const theme = useTheme()
    const [clientInfo, setClientInfo] = useState<Record<string, string | null>>(
        {}
    )

    const styles = {
        tableContainer: {
            backgroundColor: '#1E1E1E',
            boxShadow: 3,
            borderRadius: theme.shape.borderRadius,
            marginY: '1rem',
        },
        tableCell: {
            color: theme.palette.white,
        },
    }

    useEffect(() => {
        const parser = new UAParser()
        const result = parser.getResult()

        setClientInfo({
            [translate('clientBrowserLabel')]:
                result.browser.name || translate('unknown'),
            [translate('clientBrowserVersionLabel')]:
                result.browser.version || translate('unknown'),
            [translate('clientOsLabel')]:
                result.os.name || translate('unknown'),
            [translate('clientOsVersionLabel')]:
                result.os.version || translate('unknown'),
            [translate('clientLanguageLabel')]: navigator.language,
        })
    }, [translate])

    return (
        <Box sx={{ marginTop: '2rem' }}>
            <Card
                sx={{
                    margin: '0 auto',
                    padding: '1.5rem',
                    backgroundColor: '#3D3D3D',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                }}>
                <CardContent>
                    {/* Current Version Section */}
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ color: 'white' }}>
                        {translate('currentVersionTitle')}
                    </Typography>
                    <Divider
                        sx={{ marginBottom: '1rem', borderColor: 'white' }}
                    />
                    <Typography
                        variant="body1"
                        sx={{ marginBottom: '1rem', color: 'white' }}>
                        <strong>v{appVersion}</strong>
                    </Typography>

                    {/* Dependencies Section */}
                    {process.env.NODE_ENV === 'development' && (
                        <>
                            <Typography
                                variant="h5"
                                gutterBottom
                                sx={{ color: 'white' }}>
                                {translate('dependenciesTitle')}
                            </Typography>
                            <Divider
                                sx={{
                                    marginBottom: '1rem',
                                    borderColor: 'white',
                                }}
                            />
                            <Grid container spacing={2}>
                                {dependencies &&
                                    Object.entries(dependencies).map(
                                        ([pkg, version]) => (
                                            <Grid item xs={12} sm={3} key={pkg}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'white' }}>
                                                    {translate(
                                                        'dependencyNameLabel'
                                                    )}
                                                    : {pkg}
                                                    <br />
                                                    {translate(
                                                        'dependencyVersionLabel'
                                                    )}
                                                    : <strong>{version}</strong>
                                                </Typography>
                                            </Grid>
                                        )
                                    )}
                            </Grid>
                        </>
                    )}

                    {/* Client Information Section */}
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ marginTop: '2rem', color: 'white' }}>
                        {translate('clientInformationTitle')}
                    </Typography>
                    <Divider
                        sx={{ marginBottom: '1rem', borderColor: 'white' }}
                    />
                    <TableContainer component={Paper}>
                        <Table>
                            <TableBody>
                                {Object.entries(clientInfo).map(
                                    ([key, value]) => (
                                        <TableRow key={key} sx={tableRowStyle}>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                sx={styles.tableCell}>
                                                <Typography variant="body2">
                                                    {key}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={styles.tableCell}>
                                                <Typography variant="body2">
                                                    <strong>{value}</strong>
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    )
}

export { AboutApplicationSection }
